import { NgClass } from '@angular/common';
import { Tooltip } from 'primeng/tooltip';
import { Button } from 'primeng/button';
import { Ripple } from 'primeng/ripple';
import { InputText } from 'primeng/inputtext';
import { catchError, EMPTY, finalize, of, tap } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal, ViewChild } from '@angular/core';

import { KioskEmailForm } from '../../models/kiosk-auth-forms.interfaces';
import { AuthService } from '../../../../core/services/auth.service';
import { KioskRouteService } from '../../../../core/services/kiosk-route.service';
import { sanitizeReturnUrl } from '../../../../shared/utils/url.util';
import { FieldInvalidPipe } from '../../../../shared/pipes/field-invalid.pipe';
import { emailValidator } from '../../../../shared/validators/email.validator';
import { NotificationService } from '../../../../core/services/notification.service';
import { DeviceSessionService } from '../../../../core/services/device-session.service';
import { KioskPinDialogComponent } from '../kiosk-pin-dialog/kiosk-pin-dialog.component';
import { ToLowercaseDirective } from '../../../../shared/directives/to-lowercase/to-lowercase.directive';

@Component({
	selector: 'app-kiosk-sign-in-form',
	imports: [
		ReactiveFormsModule,
		InputText,
		Button,
		Ripple,
		NgClass,
		FieldInvalidPipe,
		Tooltip,
		ToLowercaseDirective,
		KioskPinDialogComponent
	],
	templateUrl: './kiosk-sign-in-form.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class KioskSignInFormComponent {
	public isLoading = signal<boolean>(false);
	public showPinDialog = signal<boolean>(false);
	public showChangePinDialog = signal<boolean>(false);

	@ViewChild('pinSignInDialog')
	private pinSignInDialog?: KioskPinDialogComponent;

	private readonly router = inject(Router);
	private readonly destroyRef = inject(DestroyRef);
	private readonly authService = inject(AuthService);
	private readonly route = inject(ActivatedRoute);
	private readonly kioskRouteService = inject(KioskRouteService);
	private readonly deviceSessionService = inject(DeviceSessionService);
	private readonly notificationService = inject(NotificationService);
	private readonly fb = inject(FormBuilder);

	private pendingEmail = '';

	public emailForm: FormGroup<KioskEmailForm> = this.fb.nonNullable.group({
		email: ['', [Validators.required, emailValidator()]]
	});

	/**
	 * Validates the email field and opens the PIN dialog.
	 *
	 * @return {void}
	 */
	public onContinue(): void {
		if (this.emailForm.invalid) {
			this.emailForm.markAllAsTouched();
			return;
		}

		this.pendingEmail = this.emailForm.getRawValue().email;
		this.showPinDialog.set(true);
	}

	/**
	 * Submits email and PIN credentials to the kiosk sign-in endpoint.
	 *
	 * @param {string} pin - Four-digit PIN entered by the user.
	 * @return {void}
	 */
	public onPinConfirmed(pin: string): void {
		this.isLoading.set(true);

		this.authService
			.kioskSignIn({ email: this.pendingEmail, pin })
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				tap(() => this.notificationService.success('Sign in successfully!')),
				catchError((errorResponse: HttpErrorResponse) => {
					this.notificationService.error(errorResponse?.error?.message, errorResponse?.error?.error);
					this.pinSignInDialog?.resetPinAndFocus();
					return of(null);
				}),
				finalize(() => this.isLoading.set(false))
			)
			.subscribe(response => {
				if (!response) {
					return;
				}

				this.showPinDialog.set(false);

				if (response.pinMustChange && this.deviceSessionService.isUserPinChangeAllowed()) {
					this.showChangePinDialog.set(true);
					return;
				}

				void this.router.navigateByUrl(this.getReturnUrl());
			});
	}

	/**
	 * Submits the new PIN and completes the kiosk sign-in flow.
	 *
	 * @param {string} pin - New four-digit PIN entered by the user.
	 * @return {void}
	 */
	public onChangePinConfirmed(pin: string): void {
		this.isLoading.set(true);

		this.authService
			.kioskChangePin({ pin })
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				tap(() => this.notificationService.success('PIN updated successfully.')),
				catchError((errorResponse: HttpErrorResponse) => {
					this.notificationService.error(errorResponse?.error?.message, errorResponse?.error?.error);
					return EMPTY;
				}),
				finalize(() => this.isLoading.set(false))
			)
			.subscribe(() => {
				this.showChangePinDialog.set(false);
				void this.router.navigateByUrl(this.getReturnUrl());
			});
	}

	/**
	 * Gets and sanitizes returnUrl from query params for the kiosk namespace.
	 *
	 * @return {string} Sanitized return URL.
	 */
	private getReturnUrl(): string {
		return sanitizeReturnUrl(this.route.snapshot.queryParams['returnUrl'], this.kioskRouteService.getDefaultHomePath());
	}
}
